/**
 * API client — thin fetch wrapper.
 *
 * NO MOCK DATA. Every call hits the real backend.
 *   Dev:  Vite proxy forwards /api → http://localhost:8787
 *   Prod: same-origin (Cloudflare Pages + Workers)
 *
 * Set VITE_API_BASE_URL only if the API lives on a different origin.
 */

import type {
  SuccessEnvelope,
  PaginatedEnvelope,
  ErrorEnvelope,
  PaginationQuery,
  Project,
  LedgerEntry,
  LedgerSummary,
  Monitor,
  Incident,
  Asset,
  DashboardSummary,
  ActivityEntry,
  IntegrationsConfig,
  TrafficSummary,
  StripeStatus,
  StripeSyncResult,
  CreateProjectBody,
  CreateLedgerEntryBody,
  CreateMonitorBody,
  CreateAssetBody,
  PatchMonitorBody,
} from '@/types/api'

export const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

/** Raised for any non-2xx response. Carries the API error code for UI mapping. */
export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details?: Record<string, unknown>

  constructor(envelope: ErrorEnvelope, status: number) {
    super(envelope.error.message)
    this.name = 'ApiError'
    this.code = envelope.error.code
    this.status = status
    this.details = envelope.error.details
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
  })

  // 204 No Content
  if (res.status === 204) return undefined as T

  const body: unknown = await res.json().catch(() => null)

  if (!res.ok) {
    // 401 → session expired, redirect to login
    if (res.status === 401 && typeof window !== 'undefined') {
      window.location.href = '/'
      throw new ApiError(
        { error: { code: 'UNAUTHORIZED', message: 'Session expired' } },
        401,
      )
    }
    const errorEnvelope = body as ErrorEnvelope | null
    if (errorEnvelope?.error) {
      throw new ApiError(errorEnvelope, res.status)
    }
    throw new ApiError(
      { error: { code: 'NETWORK_ERROR', message: `Request failed (${res.status})` } },
      res.status,
    )
  }

  return body as T
}

function buildQuery(params: object): string {
  const sp = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    sp.set(key, String(value))
  }
  const qs = sp.toString()
  return qs ? `?${qs}` : ''
}

// ─── Projects ───
export const api = {
  projects: {
    list(query: PaginationQuery & { status?: string } = {}) {
      return request<PaginatedEnvelope<Project>>(`/api/v1/projects${buildQuery(query)}`)
    },
    get(id: string) {
      return request<SuccessEnvelope<Project>>(`/api/v1/projects/${id}`)
    },
    create(body: CreateProjectBody) {
      return request<SuccessEnvelope<Project>>('/api/v1/projects', {
        method: 'POST',
        body: JSON.stringify(body),
      })
    },
    patch(id: string, body: Partial<CreateProjectBody>) {
      return request<SuccessEnvelope<Project>>(`/api/v1/projects/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      })
    },
    delete(id: string) {
      return request<void>(`/api/v1/projects/${id}`, { method: 'DELETE' })
    },
  },

  ledger: {
    list(query: {
      page?: number
      limit?: number
      direction?: string
      category?: string
      projectId?: string
      from?: string
      to?: string
    } = {}) {
      return request<PaginatedEnvelope<LedgerEntry>>(`/api/v1/ledger${buildQuery(query)}`)
    },
    summary(query: { period?: string; projectId?: string; from?: string; to?: string } = {}) {
      return request<SuccessEnvelope<LedgerSummary>>(`/api/v1/ledger/summary${buildQuery(query)}`)
    },
    create(body: CreateLedgerEntryBody) {
      return request<SuccessEnvelope<LedgerEntry>>('/api/v1/ledger', {
        method: 'POST',
        body: JSON.stringify(body),
      })
    },
  },

  monitors: {
    list(query: PaginationQuery & { projectId?: string } = {}) {
      return request<PaginatedEnvelope<Monitor>>(`/api/v1/monitors${buildQuery(query)}`)
    },
    get(id: string) {
      return request<SuccessEnvelope<Monitor>>(`/api/v1/monitors/${id}`)
    },
    create(body: CreateMonitorBody) {
      return request<SuccessEnvelope<Monitor>>('/api/v1/monitors', {
        method: 'POST',
        body: JSON.stringify(body),
      })
    },
    patch(id: string, body: PatchMonitorBody) {
      return request<SuccessEnvelope<Monitor>>(`/api/v1/monitors/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      })
    },
    delete(id: string) {
      return request<void>(`/api/v1/monitors/${id}`, { method: 'DELETE' })
    },
  },

  incidents: {
    list(query: { status?: string; projectId?: string } = {}) {
      return request<PaginatedEnvelope<Incident>>(`/api/v1/incidents${buildQuery(query)}`)
    },
    patch(id: string, body: Partial<Incident>) {
      return request<SuccessEnvelope<Incident>>(`/api/v1/incidents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      })
    },
  },

  assets: {
    list(query: { type?: string; status?: string } = {}) {
      return request<PaginatedEnvelope<Asset>>(`/api/v1/assets${buildQuery(query)}`)
    },
    create(body: CreateAssetBody) {
      return request<SuccessEnvelope<Asset>>('/api/v1/assets', {
        method: 'POST',
        body: JSON.stringify(body),
      })
    },
    delete(id: string) {
      return request<void>(`/api/v1/assets/${id}`, { method: 'DELETE' })
    },
  },

  dashboard: {
    get() {
      return request<SuccessEnvelope<DashboardSummary>>('/api/v1/dashboard')
    },
  },

  activity: {
    list(query: PaginationQuery = {}) {
      return request<PaginatedEnvelope<ActivityEntry>>(`/api/v1/dashboard/activity${buildQuery(query)}`)
    },
  },

  health: {
    get() {
      return request<{ status: string; timestamp: string; db: string; version: string }>('/health')
    },
  },

  tokens: {
    list() {
      return request<PaginatedEnvelope<{
        id: string
        name: string
        masked: string
        scopeJson: string
        lastUsedAt: string | null
        createdAt: string
      }>>('/api/v1/tokens')
    },
    create(body: { name: string; scope?: string[] }) {
      return request<SuccessEnvelope<{
        id: string
        name: string
        token: string
        scope: string[]
        createdAt: string
      }>>('/api/v1/tokens', {
        method: 'POST',
        body: JSON.stringify(body),
      })
    },
    delete(id: string) {
      return request<void>(`/api/v1/tokens/${id}`, { method: 'DELETE' })
    },
  },

  channels: {
    list() {
      return request<PaginatedEnvelope<{ id: string; type: string; name: string; configJson: string; enabled: boolean; createdAt: string }>>('/api/v1/channels')
    },
    create(body: { type: string; name: string; configJson?: string }) {
      return request<SuccessEnvelope<{ id: string; type: string; name: string }>>('/api/v1/channels', {
        method: 'POST',
        body: JSON.stringify(body),
      })
    },
    delete(id: string) {
      return request<void>(`/api/v1/channels/${id}`, { method: 'DELETE' })
    },
  },

  integrations: {
    get() {
      return request<SuccessEnvelope<IntegrationsConfig>>('/api/v1/integrations')
    },
    update(config: Record<string, Record<string, unknown>>) {
      return request<SuccessEnvelope<{ saved: boolean }>>('/api/v1/integrations', {
        method: 'PUT',
        body: JSON.stringify(config),
      })
    },
  },

  traffic: {
    list(days = 7, refresh = false) {
      const qs = refresh ? `?days=${days}&refresh=true` : `?days=${days}`
      return request<SuccessEnvelope<TrafficSummary>>(`/api/v1/traffic${qs}`)
    },
  },

  stripe: {
    status() {
      return request<SuccessEnvelope<StripeStatus>>('/api/v1/stripe/status')
    },
    sync(days = 30) {
      return request<SuccessEnvelope<StripeSyncResult>>(`/api/v1/stripe/sync?days=${days}`, {
        method: 'POST',
      })
    },
  },
}
